import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { TriangleAlert } from 'lucide-react';

import { canSee, ROLES, useSession } from '@/features/auth';
import { useElementTypes } from '@/features/elements';
import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Field, Skeleton, useToastStore } from '@/shared/ui';

import type { CampaignActivationMode, CampaignSummary } from '../model/types';
import { useCampaigns, useCreateCampaign } from '../queries/useCampaigns';
import styles from './Campaigns.module.css';
import { SurtSourcePicker } from './SurtSourcePicker';
import type { PickedSurtSource } from './SurtSourcePicker';

/**
 * Lista kampanii (spec 7A).
 *
 * ⭐ **Dwa audytoria na jednym ekranie** (spec 9.2, decyzja 81): **czyta każdy zalogowany**, bo
 * postęp kampanii jest wiedzą organizacyjną — tym samym argumentem, którym otwarty jest skład
 * zespołów. **Zakłada kampanię architekt**, bo to on ogłasza wymagania wobec produktu. Formularz
 * jest więc **ukrywany, nie blokowany** — ta sama zasada, co przy sekcjach ustawień (decyzja 78).
 *
 * ⛔ Ekran powstał **bez makiety**, za wyraźną zgodą usera (2026-08-05), w stylistyce istniejących
 * widoków — tak samo, jak zapadło przy sześciu listach z menu (decyzja 77).
 */
export function CampaignsScreen() {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const { data: user } = useSession();

  const campaigns = useCampaigns();
  const mayWrite = canSee(user, [ROLES.architect]);

  if (campaigns.isPending) {
    return (
      <div className={styles['loading']} aria-busy="true">
        <Skeleton width="20rem" height="2rem" announce />
        <Skeleton height="3rem" />
        <Skeleton height="3rem" />
      </div>
    );
  }

  if (campaigns.isError) {
    return (
      <div className={styles['failure']} role="alert">
        <p>{describeError(campaigns.error)}</p>
        <Button onClick={() => void campaigns.refetch()}>{t('common.action.retry')}</Button>
      </div>
    );
  }

  const rows = campaigns.data;

  return (
    <div className={styles['screen']}>
      <header className={styles['head']}>
        <h1 className={styles['title']}>{t('campaigns.title')}</h1>
        {rows.length > 0 ? <span className={styles['count']}>{rows.length}</span> : null}
      </header>

      <p className={styles['hint']}>{t('campaigns.lead')}</p>

      {mayWrite ? <CreateCampaignForm /> : null}

      {rows.length === 0 ? (
        <div className={styles['wrap']}>
          <p className={styles['empty']}>{t('campaigns.empty')}</p>
        </div>
      ) : (
        <div className={styles['wrap']}>
          <table className={styles['table']}>
            <thead>
              <tr>
                <th scope="col">{t('campaigns.column.title')}</th>
                <th scope="col">{t('campaigns.column.state')}</th>
                <th scope="col">{t('campaigns.column.selector')}</th>
                <th scope="col">{t('campaigns.column.task')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((campaign) => (
                <tr key={campaign.id}>
                  <td>
                    <Link className={styles['link']} to={`/campaigns/${campaign.id}`}>
                      {campaign.title}
                    </Link>
                  </td>
                  <td>
                    <CampaignState status={campaign.status} />
                  </td>
                  <td className={styles['muted']}>{describeSelector(campaign, t)}</td>
                  <td className={styles['muted']}>{campaign.template.title}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Plakietka stanu. ⚠ „Uruchomiona" znaczy **aktywna** — zbiór wciąż dogania (decyzja 84). */
export function CampaignState({ status }: { status: CampaignSummary['status'] }) {
  const { t } = useTranslation();

  const tone =
    status === 'launched' ? styles['stateActive'] : status === 'closed' ? styles['stateClosed'] : '';

  return <span className={`${styles['state']} ${tone}`}>{t(`campaigns.state.${status}`)}</span>;
}

/**
 * Zdanie opisujące zbiór. ⭐ Selektor pokazujemy **słowami, nie polami**: „serwis, tytuł zawiera
 * «płatności»" da się przeczytać, a `type=serwis; titleContains=płatności` trzeba rozszyfrować.
 */
function describeSelector(
  campaign: CampaignSummary,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  const parts: string[] = [];

  if (campaign.selector.type) {
    parts.push(t('campaigns.selector.type', { type: campaign.selector.type }));
  }

  if (campaign.selector.titleContains) {
    parts.push(t('campaigns.selector.title', { text: campaign.selector.titleContains }));
  }

  if (campaign.selector.ownerId !== null) {
    parts.push(t('campaigns.selector.owner'));
  }

  return parts.length > 0 ? parts.join(' · ') : t('campaigns.selector.everything');
}

/**
 * Zakładanie kampanii. Powstaje jako **szkic** — nic nikomu jeszcze nie spada, a wsypanie roboty
 * jest osobnym kliknięciem na ekranie kampanii, po obejrzeniu podglądu „ile trafi".
 */
function CreateCampaignForm() {
  const { t, i18n } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((s) => s.show);
  const create = useCreateCampaign();

  // ⭐ Typ ze SŁOWNIKA, nie z wolnego pola (decyzja 83). To jest dokładnie ten powód, dla którego
  // słownik w ogóle powstał: selektor dopasowuje typ **dokładną równością**, więc literówka
  // po cichu wyklucza elementy, a licznik postępu pokaże „34/120" i nie skłamie ani słowem.
  const types = useElementTypes(true);

  const [title, setTitle] = useState('');
  const [taskTitle, setTaskTitle] = useState('');
  const [type, setType] = useState('');

  // ⭐ Tryb aktywacji = WYBÓR JEDNOKROTNY, domyślnie „teraz" (decyzja 88). Poprzednie ciche
  // domyślne `iterationNext` znaczyło, że zadania kampanii są niewidoczne do odcięcia iteracji —
  // a paczek nie odcinał nikt. Odroczenie jest teraz świadomym wyborem architekta.
  const [activation, setActivation] = useState<CampaignActivationMode>('now');
  const [source, setSource] = useState<PickedSurtSource | null>(null);

  const missingSource = activation === 'surt' && source === null;

  function onSubmit(event: FormEvent) {
    event.preventDefault();

    if (title.trim() === '' || taskTitle.trim() === '' || missingSource) {
      return;
    }

    create.mutate(
      {
        title: title.trim(),
        template: {
          title: taskTitle.trim(),
          activation,
          // Źródło tylko w trybie „surt" — serwer odrzuca je poza nim (dec. 90),
          // a sprzeczne żądanie ma się nie dać wysłać zamiast być po cichu czyszczone.
          ...(activation === 'surt' && source !== null
            ? { surtSourceCommitmentId: source.commitmentId }
            : {}),
        },
        selector: { type: type === '' ? null : type },
      },
      {
        onSuccess: (campaign) => {
          setTitle('');
          setTaskTitle('');
          setActivation('now');
          setSource(null);
          show({ message: t('campaigns.created', { title: campaign.title }), tone: 'success' });
        },
      },
    );
  }

  return (
    <form className={styles['form']} onSubmit={onSubmit} noValidate>
      <h2 className={styles['sectionTitle']}>{t('campaigns.create.heading')}</h2>

      <Field
        label={t('campaigns.create.title')}
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        hint={t('campaigns.create.titleHint')}
      />

      <Field
        label={t('campaigns.create.taskTitle')}
        value={taskTitle}
        onChange={(event) => setTaskTitle(event.target.value)}
        hint={t('campaigns.create.taskTitleHint')}
      />

      <label className={styles['checkbox']}>
        {t('campaigns.create.type')}
        <select value={type} onChange={(event) => setType(event.target.value)}>
          <option value="">{t('campaigns.create.anyType')}</option>
          {(types.data ?? []).map((entry) => (
            <option key={entry.id} value={entry.name}>
              {entry.name}
            </option>
          ))}
        </select>
      </label>

      <fieldset className={styles['modes']}>
        <legend className={styles['modesLegend']}>{t('campaigns.create.activation.legend')}</legend>
        {(['now', 'iterationNext', 'surt'] as const).map((mode) => (
          <label key={mode} className={styles['checkbox']}>
            <input
              type="radio"
              name="activation"
              value={mode}
              checked={activation === mode}
              onChange={() => {
                setActivation(mode);
                if (mode !== 'surt') {
                  setSource(null);
                }
              }}
            />
            {t(`campaigns.create.activation.${mode}`)}
          </label>
        ))}
        <p className={styles['muted']}>{t(`campaigns.create.activation.${activation}Hint`)}</p>

        {/* Ostrzeżenie jest OSOBNYM komunikatem, nie ⚠ wklejonym w środek akapitu
            (tak było do 2026-08-06). ⭐ Warunek pyta i18n, czy klucz istnieje, więc
            dołożenie ostrzeżenia do kolejnego trybu jest dopisaniem jednego klucza
            do przekładu — bez dotykania tego komponentu. */}
        {i18n.exists(`campaigns.create.activation.${activation}Warning`) ? (
          <p className={styles['warnLine']}>
            <TriangleAlert size={15} aria-hidden="true" />
            {t(`campaigns.create.activation.${activation}Warning`)}
          </p>
        ) : null}
      </fieldset>

      {activation === 'surt' ? (
        <>
          <SurtSourcePicker value={source} onChange={setSource} />
          {missingSource ? (
            <p className={styles['muted']}>{t('campaigns.create.surtSource.missing')}</p>
          ) : null}
        </>
      ) : null}

      {create.isError ? (
        <p className={styles['muted']} role="alert">
          {describeError(create.error)}
        </p>
      ) : null}

      <div className={styles['actions']}>
        <Button type="submit" variant="primary" disabled={create.isPending || missingSource}>
          {t('campaigns.create.submit')}
        </Button>
      </div>
    </form>
  );
}
